import { useState, useEffect } from "react";
import NudgeExpanded from "./NudgeExpanded";
import NudgeCompressed from "./NudgeCompressed";
import { getDataLayer } from "utils/analytics";

type NudgeProps = {
  cap: number;
  displayCap: number;
  pageViews: number;
  setSlidingDirection: (slidingDirection: "none" | "up" | "down") => void;
};

const Nudge = ({
  cap,
  displayCap,
  pageViews,
  setSlidingDirection,
}: NudgeProps) => {
  const count = cap - pageViews;
  const displayCount = displayCap - pageViews;
  const lastArticle = count === 1;
  const penultimateArticle = count === 2;
  const firstArticle = pageViews === 1;
  const shouldExpandBool = firstArticle || lastArticle;
  const [showExpanded, setShowExpanded] = useState(shouldExpandBool);
  const [manuallyExpanded, setManuallyExpanded] = useState(false);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > 100 && !manuallyExpanded && showExpanded) {
        //add class to paywall element for transition effect
        setSlidingDirection("down");
        timeout = setTimeout(() => {
          setShowExpanded(false);
          setSlidingDirection("none");
        }, 100);
        getDataLayer().push({
          event: "Nudge Scroll",
          category: "nudge interaction",
          action: "nudge:scroll_collapse",
          label: "nudge-compressed",
        });
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (timeout) clearTimeout(timeout);
    };
  }, [showExpanded, manuallyExpanded, setSlidingDirection]);

  //logic for messages to display nudgeSequence goes here
  //we're including logic for 5 article views, despite the three article cap, because the holdout test will be live through 2020
  //since the logic isn't the same for each, not sure how to abstract and reuse this?
  let title = "";
  if (lastArticle) {
    title = "Never miss a story from The Atlantic.";
  } else if (firstArticle) {
    title = "Thank you for reading The Atlantic.";
  } else {
    title = "Discover new ideas. Rethink old assumptions.";
  }

  let subtitle = "";
  if (lastArticle) {
    subtitle = "Get unlimited access to all of our journalism.";
  } else if (firstArticle) {
    subtitle = "Enjoy unlimited access to all of our journalism.";
  } else {
    subtitle = "Get unlimited access to The Atlantic.";
  }

  let countText = "";
  if (lastArticle) {
    countText = "This is your last free article.";
  } else if (penultimateArticle) {
    countText = "more article this month";
  } else {
    countText = "more articles this month";
  }

  let compressedTitle = "";
  if (lastArticle) {
    compressedTitle = "This is your last free article.";
  } else if (firstArticle) {
    compressedTitle = "Enjoy unlimited access to The Atlantic.";
  } else if (penultimateArticle) {
    compressedTitle = "more free article this month";
  } else {
    compressedTitle = "more free articles this month";
  }

  if (showExpanded) {
    return (
      <NudgeExpanded
        title={title}
        subtitle={subtitle}
        count={displayCount}
        pageViews={pageViews}
        lastArticle={lastArticle}
        countText={countText}
        setShowExpanded={setShowExpanded}
        setSlidingDirection={setSlidingDirection}
      />
    );
  } else {
    return (
      <NudgeCompressed
        title={compressedTitle}
        count={displayCount}
        pageViews={pageViews}
        firstArticle={firstArticle}
        lastArticle={lastArticle}
        mobileTitle="Subscribe for unlimited access"
        setShowExpanded={setShowExpanded}
        setManuallyExpanded={setManuallyExpanded}
        setSlidingDirection={setSlidingDirection}
      />
    );
  }
};

export default Nudge;
